"use client"

import React, { useEffect, useMemo } from "react"
import cx from "classnames"
import { useFreezeBodyScroll } from "hooks"
import { Message } from "@locmod/intl"
import { useMedia } from "@/contexts"
import { Icon } from "components/ui"
import { Overlay } from "components/layout"
import messages from "./messages"
// import type { OvertimeMarketProps } from "./components/Market/OvertimeMarket"
import OvertimeMarket, {
  OvertimeMarketSkeleton,
} from "./components/Market/OvertimeMarket"
import { getTitleText } from "@/Overtime/utils/marketsV2"
import { FUTURES_MARKET_TYPES } from "overtime-utils"
import { isSgpMarket } from "@/Overtime/utils/sgpHelpers"

interface MarketData {
  odds: any[]
  positionNames: string[]
  [key: string]: any // for other properties that might exist
}

interface Participant {
  name: string
}

interface ProcessedMarket extends MarketData {
  participants: Participant[]
}

interface OvertimeMarketProps {
  game: MarketData
  section: string
}

const createMarketFromOdds = (
  market: MarketData,
  startIdx: number,
  endIdx: number
): MarketData | ProcessedMarket => {
  if (!market?.odds || !market?.positionNames) {
    return market
  }

  const selectedOdds = market.odds.slice(startIdx, endIdx)
  const selectedPositions = market.positionNames.slice(startIdx, endIdx)

  return {
    ...market,
    odds: selectedOdds,
    positionNames: selectedPositions,
    participants: selectedPositions.map((name: string) => ({ name })),
  }
}

const shouldSplitMarket = (market: any) => {
  return (
    FUTURES_MARKET_TYPES.includes(market?.typeId) &&
    market?.positionNames?.length > 2
  )
}

const hasValidOdds = (market: MarketData): boolean => {
  return market?.odds?.some((odd: number) => odd !== 0) ?? false
}

const isCorrectScoreMarket = (market: any) => {
  return market?.type === "correctScore" || market?.typeId === 10100
}

const processMarket = (market: MarketData): MarketData | ProcessedMarket => {
  if (!market) {
    return market
  }

  // Handle SGP markets - ensure they are processed correctly
  if (isSgpMarket(market)) {
    // For SGP markets, ensure we preserve the combinedPositions structure
    return {
      ...market,
      // Keep the original structure for SGP markets
      odds: market.odds || [],
      positionNames: market.positionNames || [],
      participants:
        market.positionNames?.map((name: string) => ({ name })) || [],
    }
  }

  // Handle correct score markets - limit to top options with non-zero odds
  if (isCorrectScoreMarket(market) && market?.positionNames?.length > 6) {
    const validOddsIndices = market.odds
      .map((odd: number, index: number) => ({ odd, index }))
      .filter((item: any) => item.odd > 0)
      .sort((a: any, b: any) => b.odd - a.odd) // Sort by odds descending
      .slice(0, 6) // Take top 6 options
      .map((item: any) => item.index)
      .sort((a: number, b: number) => a - b) // Sort indices back to original order

    if (validOddsIndices.length > 0) {
      const selectedOdds = validOddsIndices.map(index => market.odds[index])
      const selectedPositions = validOddsIndices.map(
        index => market.positionNames[index]
      )

      return {
        ...market,
        odds: selectedOdds,
        positionNames: selectedPositions,
        participants: selectedPositions.map((name: string) => ({ name })),
      }
    }
  }

  // Handle futures markets
  if (shouldSplitMarket(market)) {
    return createMarketFromOdds(market, 0, 2)
  }

  return market
}

const HeadMarket: React.FC<OvertimeMarketProps> = ({ game, section }) => {
  const marketToShow = useMemo((): MarketData => {
    if (hasValidOdds(game)) {
      return game
    }

    return (game.childMarkets?.[0] || game) as MarketData
  }, [game])

  const name = useMemo((): string | undefined => {
    return getTitleText(marketToShow as any)
  }, [marketToShow])

  const processedMarket = useMemo((): MarketData | ProcessedMarket => {
    return processMarket(marketToShow)
  }, [marketToShow])

  return (
    <OvertimeMarket
      childMarkets={processedMarket as any}
      game={game}
      section={section}
      marketName={name || processedMarket?.type}
    />
  )
}

type OtherMarketsProps = {
  market: any
  game: any
  isShownInHead: boolean
}

const OtherMarkets: React.FC<OtherMarketsProps> = ({
  market,
  game,
  isShownInHead,
}) => {
  const name = useMemo(() => {
    return getTitleText(market)
  }, [market])

  const processedMarket = useMemo(() => {
    return processMarket(market)
  }, [market])

  // Don't render if this market is already shown in head
  if (isShownInHead) {
    return null
  }

  return (
    <OvertimeMarket
      key={`${market.gameId}-${market.typeId}-${market.line}`}
      childMarkets={processedMarket as any}
      game={game}
      marketName={name || market.type}
      section="All Markets"
    />
  )
}

type MobileMarketsProps = {
  markets: any[]
  game: any
  onClose: () => void
  isMarketShownInHead: (market: any) => boolean
}

const MobileMarkets: React.FC<MobileMarketsProps> = ({
  markets,
  game,
  onClose,
  isMarketShownInHead,
}) => {
  useFreezeBodyScroll()

  const handleContentClick = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <Overlay className="flex items-end" onClick={onClose}>
      <div
        className="relative w-full bg-accent-grey-1000 rounded-t-min px-4 pb-4 max-h-[70vh] overflow-auto no-scrollbar"
        onClick={handleContentClick}
      >
        <div className="flex items-center justify-between p-4 -mx-4 bg-accent-grey-1000 sticky top-0 z-10">
          <Message
            className="text-caption-13 font-semibold"
            value={messages.markets}
          />
          <button
            className="size-4 text-accent-grey-30"
            onClick={onClose}
            aria-label="Close markets"
          >
            <Icon name="interface/close" />
          </button>
        </div>
        {markets.map(market => (
          <OtherMarkets
            key={`${market.gameId}-${market.typeId}-${market.line}`}
            market={market}
            game={game}
            isShownInHead={isMarketShownInHead(market)}
          />
        ))}
      </div>
    </Overlay>
  )
}

export const MarketsSkeleton: React.FC = () => {
  return (
    <div className="ds:p-2 border border-transparent">
      <div className="flex">
        <OvertimeMarketSkeleton />
        <div className="bone size-7 mt-5 ml-2 rounded-min flex-none border border-transparent" />
      </div>
    </div>
  )
}

type ContentProps = {
  markets: any[]
  game: any
  headMarket: any
  isMarketShownInHead: (market: any) => boolean
}

const Content: React.FC<ContentProps> = ({
  markets,
  game,
  headMarket,
  isMarketShownInHead,
}) => {
  const [isOpen, setOpen] = React.useState(false)
  const { isMobileView } = useMedia()
  const contentRef = React.useRef<HTMLDivElement>(null)

  const isDisabled = !markets.length

  const contentClassName = cx(
    "w-full flex mb:border-transparent ds:p-2 border",
    {
      "absolute overflow-y-auto no-scrollbar max-h-[20rem] border-accent-grey-800 pb-2 z-30 rounded-md bg-accent-grey-950":
        isOpen && !isMobileView,
      "border-transparent": !isOpen,
    }
  )

  const buttonClassName = cx(
    "size-7 flex items-center justify-center flex-none rounded-min",
    "group border-[1px] border-accent-grey-700 text-accent-grey-100 ml-2 mt-5 sticky top-5",
    "disabled:cursor-not-allowed disabled:text-accent-grey-700 disabled:border-accent-grey-800",
    {
      "hover:text-accent-grey-10 hover:border-accent-grey-400": !isDisabled,
    }
  )

  const handleClose = () => {
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen(prev => !prev)
  }

  useMemo(() => {
    if (!isMobileView && !isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [isOpen, isMobileView])

  useEffect(() => {
    if (isMobileView && !isOpen) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const composedPath = event.composedPath()

      if (!composedPath.includes(contentRef.current!)) {
        setOpen(false)
      }
    }

    // should be capture to avoid immediate triggering
    document.addEventListener("click", handleOutsideClick, { capture: true })

    return () => {
      document.removeEventListener("click", handleOutsideClick, {
        capture: true,
      })
    }
  }, [isOpen, isMobileView])

  return (
    <div className="w-full ds:relative ds:h-16">
      <div ref={contentRef} className={contentClassName}>
        <div className="w-full h-fit">
          {/* Display head market */}
          <HeadMarket game={game} section="Main Markets" />

          {isOpen && (
            <>
              {isMobileView ? (
                <MobileMarkets
                  markets={markets}
                  game={game}
                  onClose={handleClose}
                  isMarketShownInHead={isMarketShownInHead}
                />
              ) : (
                markets.map(market => (
                  <OtherMarkets
                    key={`${market.gameId}-${market.typeId}-${market.line}`}
                    market={market}
                    game={game}
                    isShownInHead={isMarketShownInHead(market)}
                  />
                ))
              )}
            </>
          )}
        </div>
        <button
          className={buttonClassName}
          disabled={isDisabled}
          onClick={handleToggle}
        >
          <Icon
            className={cx("size-4", {
              "rotate-180 text-accent-grey-30": isOpen,
            })}
            name="interface/chevron_down"
          />
        </button>
      </div>
    </div>
  )
}

type MarketsProps = {
  game: any
}

const Markets: React.FC<MarketsProps> = ({ game }) => {
  const headMarket = useMemo(() => {
    return hasValidOdds(game) ? game : game.childMarkets?.[0]
  }, [game])

  const markets = useMemo(() => {
    // If game has childMarkets, use them but exclude the head market
    if (game?.childMarkets?.length > 0) {
      // If head market is the main game, return all child markets
      if (hasValidOdds(game)) {
        return game.childMarkets
      }
      // If head market is first child market, return remaining child markets
      return game.childMarkets.slice(1)
    }
    // If it's a futures game with multiple positions, treat as a market that needs splitting
    if (shouldSplitMarket(game)) {
      return [game]
    }
    // For all other games with odds, return empty array since main market is head
    return []
  }, [game, headMarket])

  // Check if a market is the same as the head market
  const isMarketShownInHead = (market: any) => {
    if (!headMarket) {
      return false
    }
    return (
      market.gameId === headMarket.gameId &&
      market.typeId === headMarket.typeId &&
      market.line === headMarket.line
    )
  }

  // Filter markets to include SGP markets
  const filteredMarkets = useMemo(() => {
    if (!markets.length) {
      return []
    }

    // Include all markets, but ensure SGP markets are properly detected
    return markets.map(market => ({
      ...market,
      // Ensure SGP markets have proper detection
      isSgpMarket: isSgpMarket(market),
    }))
  }, [markets])

  return (
    <Content
      markets={filteredMarkets}
      game={game}
      headMarket={headMarket}
      isMarketShownInHead={isMarketShownInHead}
    />
  )
}

export default Markets
